import { useEffect, useRef, useState } from "react";
import { getToken } from "../lib/auth";

interface UseWebSocketOptions<T> {
  url: string | null;
  onMessage: (event: T) => void;
  reconnectDelayMs?: number;
}

export function useWebSocket<T = unknown>({
  url,
  onMessage,
  reconnectDelayMs = 2000,
}: UseWebSocketOptions<T>): {
  connected: boolean;
  send: (data: string) => void;
} {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const onMessageRef = useRef(onMessage);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!url) return;
    let closed = false;

    const connect = () => {
      const token = getToken();
      if (!token) return;
      const sep = url.includes("?") ? "&" : "?";
      const full = `${url}${sep}token=${encodeURIComponent(token)}`;
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(
        full.startsWith("ws") ? full : `${protocol}//${window.location.host}${full}`,
      );
      wsRef.current = ws;

      ws.addEventListener("open", () => setConnected(true));
      ws.addEventListener("close", () => {
        setConnected(false);
        if (closed) return;
        reconnectTimerRef.current = window.setTimeout(connect, reconnectDelayMs);
      });
      ws.addEventListener("error", () => {
        try {
          ws.close();
        } catch {
          /* noop */
        }
      });
      ws.addEventListener("message", (event) => {
        try {
          const payload = JSON.parse(event.data) as T;
          onMessageRef.current(payload);
        } catch {
          /* ignore non-JSON */
        }
      });
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current);
      }
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [url, reconnectDelayMs]);

  return {
    connected,
    send: (data) => wsRef.current?.send(data),
  };
}
